import { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useUi } from '@/store/ui';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { CommandPalette } from './CommandPalette';
import { NotificationsPanel } from './NotificationsPanel';
import { ChatFab } from './ChatFab';
import { useSocket } from '@/realtime/socketContext';
import { IncomingCall } from '@/components/call/IncomingCall';
import { WalkieHud } from '@/components/chat/WalkieHud';
import { PersistentCallPlayer } from '@/components/call/PersistentCallPlayer';
import { AnnouncementBanner } from '@/components/team/AnnouncementBanner';
import { RtcSessionProvider } from '@/media/RtcSessionProvider';
import { Spinner } from '@/components/ui';
import { getProductSurface } from '@/lib/product';
import { effectiveProductSurface, hasFullAccess } from '@/lib/product';
import { useAuth } from '@/store/auth';

const ACTIVITY_LABELS: [RegExp, string][] = [
  [/^\/spaces\//, 'In a space'],
  [/^\/meetings\//, 'In a meeting'],
  [/^\/games\//, 'Playing a game'],
  [/^\/projects\//, 'Working on a project'],
  [/^\/my-tasks/, 'Reviewing tasks'],
  [/^\/chat/, 'Using chat'],
  [/^\/whiteboards/, 'Using a whiteboard'],
  [/^\/files/, 'Browsing files'],
  [/^\/people/, 'Viewing the team'],
  [/^\/$/, 'On the dashboard'],
];

export function AppShell() {
  const { sidebarOpen, sidebarCollapsed, language, setSidebar, setCommandPalette } = useUi();
  const isDesktop = useIsDesktop();
  const { pathname } = useLocation();
  const { socket, connected } = useSocket();
  const features = useAuth((state) => state.features);
  const disabledFeatures = useAuth((state) => state.disabledFeatures);
  const fullAccess = hasFullAccess(features, disabledFeatures);
  const product = effectiveProductSurface(getProductSurface(), features);

  useEffect(() => {
    if (!socket || !connected) return;
    const label =
      ACTIVITY_LABELS.find(([pattern]) => pattern.test(pathname))?.[1] ?? 'Using Itsappy Lab';
    socket.emit('activity:set', { label, path: pathname });
  }, [socket, connected, pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandPalette(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setCommandPalette]);

  /* Crossing the breakpoint resets the sidebar to that layout's default. */
  useEffect(() => {
    setSidebar(isDesktop);
  }, [isDesktop, setSidebar]);

  /* The drawer is modal, so Escape should dismiss it. */
  useEffect(() => {
    if (isDesktop || !sidebarOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSidebar(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDesktop, sidebarOpen, setSidebar]);

  return (
    <RtcSessionProvider>
      {/* `relative` makes the shell the containing block for any absolutely
          positioned element with no positioned ancestor (sr-only labels,
          mostly), so its `overflow-hidden` clips them instead of the page
          growing taller than the window and scrolling the whole app. */}
      <div className="relative flex h-full overflow-hidden bg-surface-0">
        {product === 'lab' && sidebarOpen &&
          (isDesktop ? (
            <Sidebar collapsed={sidebarCollapsed} />
          ) : (
            <>
              <div
                className="fixed inset-0 z-40 animate-fade-in bg-black/60 backdrop-blur-sm"
                onClick={() => setSidebar(false)}
              />
              <div
                className={`fixed inset-y-0 z-50 flex animate-slide-in-left ${language === 'ar' ? 'right-0' : 'left-0'}`}
              >
                <Sidebar collapsed={false} onNavigate={() => setSidebar(false)} />
              </div>
            </>
          ))}
        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar />
          {product === 'lab' && fullAccess && <AnnouncementBanner />}
          <main className="min-h-0 flex-1 overflow-hidden">
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center" role="status">
                  <Spinner className="h-6 w-6" />
                  <span className="sr-only">Loading page</span>
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </main>
        </div>
        {product === 'lab' && fullAccess && <CommandPalette />}
        {/* Gated on the surface alone, matching the bell in `Topbar` — a focused
            account still gets mentioned and still gets assigned work, so tying the
            panel to `fullAccess` left them a bell that opened nothing. */}
        {product === 'lab' && <NotificationsPanel />}
        <PersistentCallPlayer />
        {product === 'lab' && features.includes('chat') && <ChatFab />}
        {product !== 'meet' && features.includes('chat') && <IncomingCall />}
        {/* The radio plays wherever you are, so it says so wherever you are. */}
        {features.includes('chat') && <WalkieHud />}
      </div>
    </RtcSessionProvider>
  );
}
